import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { signaturesAPI } from '../api';
import { toast } from 'sonner';
import { 
  PenLine, Plus, Trash2, Star, Eye, Loader2, Edit, 
  Building, Phone, Mail, Globe, Linkedin, Twitter, MapPin
} from 'lucide-react';

export const SignatureSettingsPage = () => {
  const [signatures, setSignatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [editingSignature, setEditingSignature] = useState(null);
  
  const [formData, setFormData] = useState({
    name: 'Default Signature',
    is_default: true,
    full_name: '',
    title: '',
    company: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    address: '',
    logo_url: '',
    linkedin_url: '',
    twitter_url: '',
    custom_html: '',
    template_style: 'professional'
  });

  useEffect(() => {
    fetchSignatures();
  }, []);

  const fetchSignatures = async () => {
    try {
      const res = await signaturesAPI.getAll();
      setSignatures(res.data || []);
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSignature) {
        await signaturesAPI.update(editingSignature.id, formData);
        toast.success('Signature updated');
      } else {
        await signaturesAPI.create(formData);
        toast.success('Signature created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchSignatures();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save signature');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this signature?')) return;
    try {
      await signaturesAPI.delete(id);
      toast.success('Signature deleted');
      fetchSignatures();
    } catch (error) {
      toast.error('Failed to delete signature');
    }
  };

  const handlePreview = async (signature) => {
    try {
      const res = await signaturesAPI.preview(signature.id);
      setPreviewHtml(res.data.html);
      setIsPreviewOpen(true);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const openEdit = (signature) => {
    setEditingSignature(signature);
    setFormData({
      name: signature.name || 'Default Signature',
      is_default: signature.is_default || false,
      full_name: signature.full_name || '',
      title: signature.title || '',
      company: signature.company || '',
      phone: signature.phone || '',
      mobile: signature.mobile || '',
      email: signature.email || '',
      website: signature.website || '',
      address: signature.address || '',
      logo_url: signature.logo_url || '',
      linkedin_url: signature.linkedin_url || '',
      twitter_url: signature.twitter_url || '',
      custom_html: signature.custom_html || '',
      template_style: signature.template_style || 'professional'
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingSignature(null);
    setFormData({
      name: 'Default Signature',
      is_default: signatures.length === 0,
      full_name: '',
      title: '',
      company: '',
      phone: '',
      mobile: '',
      email: '',
      website: '',
      address: '',
      logo_url: '',
      linkedin_url: '',
      twitter_url: '',
      custom_html: '',
      template_style: 'professional'
    });
  };

  const styleOptions = [
    { value: 'professional', label: 'Professional', desc: 'Clean with blue accent border' },
    { value: 'minimal', label: 'Minimal', desc: 'Simple one-line format' },
    { value: 'modern', label: 'Modern', desc: 'Gradient background with icons' },
    { value: 'classic', label: 'Classic', desc: 'Traditional serif style' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Signatures</h1>
          <p className="text-muted-foreground">Create professional signatures for your emails</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="create-signature-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Signature
        </Button>
      </div>

      {/* Signatures List */}
      {signatures.length > 0 ? (
        <div className="grid gap-4">
          {signatures.map(sig => (
            <Card key={sig.id} className={sig.is_default ? 'border-blue-200 bg-blue-50/30' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="bg-slate-100 p-3 rounded-full">
                      <PenLine className="h-6 w-6 text-slate-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{sig.name}</h3>
                        {sig.is_default && (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Star className="h-3 w-3 mr-1" />
                            Default
                          </Badge>
                        )}
                        <Badge variant="secondary">{sig.template_style}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {sig.full_name}{sig.title ? ` • ${sig.title}` : ''}{sig.company ? ` at ${sig.company}` : ''}
                      </p>
                      {sig.email && (
                        <p className="text-xs text-muted-foreground mt-1">{sig.email}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handlePreview(sig)} data-testid={`preview-sig-${sig.id}`}>
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(sig)} data-testid={`edit-sig-${sig.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600" onClick={() => handleDelete(sig.id)} data-testid={`delete-sig-${sig.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6 text-center">
            <div className="bg-slate-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <PenLine className="h-8 w-8 text-slate-500" />
            </div>
            <h3 className="text-lg font-semibold">No Signatures Yet</h3>
            <p className="text-muted-foreground mb-4">Create your first email signature</p>
            <Button onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Signature
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingSignature ? 'Edit Signature' : 'Create Signature'}</DialogTitle>
            <DialogDescription>
              Fill in your details to create a professional email signature
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <Tabs defaultValue="info" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Basic Info</TabsTrigger>
                <TabsTrigger value="contact">Contact</TabsTrigger>
                <TabsTrigger value="style">Style</TabsTrigger>
              </TabsList>
              
              <TabsContent value="info" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Signature Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder="e.g., Work Signature"
                      data-testid="sig-name-input"
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-7">
                    <Switch
                      id="default-sig"
                      checked={formData.is_default}
                      onCheckedChange={(v) => setFormData(p => ({ ...p, is_default: v }))}
                    />
                    <Label htmlFor="default-sig">Set as default signature</Label>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name</Label>
                    <Input
                      value={formData.full_name}
                      onChange={(e) => setFormData(p => ({ ...p, full_name: e.target.value }))}
                      placeholder="John Smith"
                      data-testid="sig-fullname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Job Title</Label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))}
                      placeholder="Sales Manager"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData(p => ({ ...p, company: e.target.value }))}
                    placeholder="Acme Corporation"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="contact" className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Office Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Phone className="h-4 w-4" /> Mobile</Label>
                    <Input
                      value={formData.mobile}
                      onChange={(e) => setFormData(p => ({ ...p, mobile: e.target.value }))}
                      placeholder="+1 (555) 987-6543"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Mail className="h-4 w-4" /> Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      placeholder="john@company.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Globe className="h-4 w-4" /> Website</Label>
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))}
                      placeholder="https://company.com"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><MapPin className="h-4 w-4" /> Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                    placeholder="123 Business St, City, State"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Linkedin className="h-4 w-4" /> LinkedIn URL</Label>
                    <Input
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData(p => ({ ...p, linkedin_url: e.target.value }))}
                      placeholder="https://linkedin.com/in/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><Twitter className="h-4 w-4" /> Twitter URL</Label>
                    <Input
                      value={formData.twitter_url}
                      onChange={(e) => setFormData(p => ({ ...p, twitter_url: e.target.value }))}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                </div>
              </TabsContent>
              
              <TabsContent value="style" className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Template Style</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {styleOptions.map(opt => (
                      <div
                        key={opt.value}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          formData.template_style === opt.value 
                            ? 'border-blue-500 bg-blue-50' 
                            : 'hover:border-slate-300'
                        }`}
                        onClick={() => setFormData(p => ({ ...p, template_style: opt.value }))}
                      >
                        <p className="font-medium">{opt.label}</p>
                        <p className="text-xs text-muted-foreground">{opt.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Custom HTML (Advanced)</Label>
                  <Textarea
                    value={formData.custom_html}
                    onChange={(e) => setFormData(p => ({ ...p, custom_html: e.target.value }))}
                    placeholder="<div>Custom signature HTML...</div>"
                    rows={4}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    If provided, custom HTML will override the template style
                  </p>
                </div>
              </TabsContent>
            </Tabs>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-signature-btn">
                {editingSignature ? 'Update Signature' : 'Create Signature'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Signature Preview</DialogTitle>
            <DialogDescription>
              This is how your signature will appear in emails
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white p-4 border rounded-lg">
            <p className="text-sm text-muted-foreground mb-4">Best regards,</p>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
          <DialogFooter>
            <Button onClick={() => setIsPreviewOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignatureSettingsPage;
